import React, { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import moment from 'moment'

import { useServiceScheduleContext, actionTypes } from '../../context/serviceScheduleContext'

import Button from '../button'
import Steps from '../steps'
import { assetPrefix } from 'config/env_config'

import * as S from './styles'
import { usePetAndServicesContext } from '../../context/petAndServicesContext'

import { PAGE_URL } from 'src/utils'

const ONLINE_PAYMENT_DEADLINE = process.env.ONLINE_PAYMENT_DEADLINE
const SCHEDULING_MIN_HOURS = process.env.SCHEDULING_MIN_HOURS

const FormSteps = ({ children, finalStep, context }) => {
  const { state, steps, dispatch, functions, editSteps } = useServiceScheduleContext()
  const {
    activeStep,
    review,
    isHowToPay,
    invertStep,
    scheduleBody,
    professionalData,
    partnersData,
    formList
  } = state

  const {
    pricesInfo,
    handleAddPetToScheduling,
    handleConfirmScheduling,
    handleUnselectPet,
    hasSchedulingChanged
  } = functions

  const { functions: petFunctions } = usePetAndServicesContext()
  const { retrievePetsData } = petFunctions

  const router = useRouter()

  const [numberOfSteps, setNumberOfsteps] = useState(7)

  const currentStep = steps.find((step) => step.step === activeStep)
  const nextDisabled = currentStep.disabledRule
  const nextHidden = currentStep.nextHiddenRule

  const backButtonProps = {
    className: 'backButton',
    textDecoration: 'underline',
    label: 'Voltar'
  }

  const scheduleForMoreOnePetProps = {
    border: 'var(--cobasi-blue-light-9)',
    label: 'Agendar mais 1 pet'
  }

  const nextButtonProps = {
    className: 'nextButton',
    disabled: nextDisabled,
    icon: `${assetPrefix}/static/icons/${nextDisabled ? 'arrow-right-disabled' : 'arrow-right'}.svg`,
    label: 'Avançar'
  }

  const confirmScheduleProps = {
    label: 'Confirmar agendamento'
  }

  useEffect(() => {
    setNumberOfsteps(steps?.length)
  }, [steps])

  useEffect(() => {
    const footerElement = document.getElementById('footer')
    if (footerElement && activeStep === 1 && !nextDisabled) {
      footerElement.style.marginBottom = '90px'
    } else if (footerElement) {
      footerElement.style.marginBottom = '88px'
    }

    setNumberOfsteps(steps?.length)

    return () => {
      if (footerElement) {
        footerElement.style.marginBottom = ''
      }
    }
  }, [steps, nextHidden])

  const onUpdateScheduling = (value) => { dispatch({ type: actionTypes.UPDATE_STATE, payload: value }) }

  const onGoTo = (path) => {
    const stepToSet = steps.find(item => item.path === path)

    dispatch({ type: actionTypes.UPDATE_STATE, payload: { activeStep: stepToSet?.step } })

    const query = { ...router.query, showDiary: false }

    router.push({
      pathname: `/agendamentos${path}`,
      query: query
    })
  }

  const calcNextStep = (actualStep) => {
    let stepsToCheck = steps

    if (invertStep && scheduleBody.selectTime && scheduleBody.selectDay) {
      stepsToCheck = stepsToCheck.map(item => item.path === '/data' ? ({ ...item, active: true }) : item)
    }

    const remainingSteps = stepsToCheck.filter((step) => step.step > actualStep && !step.active && step.step !== 7
    ).sort((a, b) => a.step - b.step)

    if (remainingSteps.length === 1 && remainingSteps[0].step === 6) {
      onUpdateScheduling({
        activeStep: steps.length - 2,
        review: true
      })

      onGoTo(steps[steps.length - 2].path)
      return
    }

    if (!remainingSteps.length) {
      onUpdateScheduling({
        activeStep: steps.length - 1,
        isHowToPay: true,
        review: false
      })

      onGoTo(steps[steps.length - 1].path)
      return
    }

    onUpdateScheduling({
      activeStep: remainingSteps[0]?.step
    })

    onGoTo(remainingSteps[0]?.path)
  }

  const editScheduling = (step = 1, isCustomState = false) => {
    const redirectedStep = steps.find(schedulingStep => schedulingStep.step === step)
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        ...editSteps[redirectedStep.id],
        ...(isCustomState && {
          activeStep: 2,
          scheduleBody: {
            ...editSteps[redirectedStep.id].scheduleBody,
            professional_id: ''
          }
        })
      }
    })

    if (isCustomState) {
      const locationStep = steps.find(schedulingStep => schedulingStep.step === 2)

      onGoTo(locationStep.path)
    } else {
      onGoTo(redirectedStep.path)
    }
  }

  useEffect(() => {
    if (!router.route.includes(steps[activeStep - 1].path)) {
      onGoTo(steps.find(item => item.step === (activeStep))?.path)
    }
  }, [invertStep])

  const redirectToServices = async () => {
    if (!scheduleBody.pet_id && state.formList.length) {
      const formlistLength = state.formList.length
      onUpdateScheduling({
        ...state.formList[formlistLength - 1].data,
        activeStep: 5,
        review: true
      })

      onGoTo(steps[5].path)

      return
    }

    if (scheduleBody.pet_id) {
      await retrievePetsData({ context })
      return handleUnselectPet()
    }
    window.localStorage.removeItem('category')
    window.localStorage.removeItem('schedulingState')
    window.localStorage.removeItem('paymentState')

    await router.push('/')
  }

  const handleClickBackButton = async () => {
    if (activeStep === 1) {
      await redirectToServices()
    } else {
      // quando for multiplo agendamento, ao selecionar a mesma data do pet anterior e clicar em voltar no step de profissional precisa ser redirecionado para seleção de loja e não de data
      if ((activeStep - 1 === 4) && state?.scheduleBody?.samePetDate && state?.formList?.length > 0) {
        const isCustomState = true
        editScheduling(5, isCustomState)
      } else {
        editScheduling(activeStep - 1)
      }
    }
  }

  const handleScheduleForMoreOnePet = () => {
    const {
      currentPriceWithDiscount
    } = pricesInfo()

    handleAddPetToScheduling(currentPriceWithDiscount, onGoTo)
  }

  return (
    <>
      {
        (!finalStep && !isHowToPay) && (
          <S.StepsContainer>
            <Steps steps={steps.slice(0, 5)} activeStep={activeStep} numberOfSteps={numberOfSteps} />
          </S.StepsContainer>
        )
      }

      {children}

      <S.WrapperSchedulingActions finalStep={review} firstStep={currentStep.step === 1}>
        <div>
          <>
            <Button
              {...((!finalStep || !review) ? backButtonProps : scheduleForMoreOnePetProps)}
              fontColor='var(--cobasi-blue-light-9)'
              color='var(--the-white)'
              margin='0'
              onClick={(!finalStep || !review) ? () => handleClickBackButton() : () => handleScheduleForMoreOnePet()}
            />
            <Button
              {...((!finalStep || !review)) ? nextButtonProps : confirmScheduleProps}
              color='var(--cobasi-blue-light-9)'
              fontColor='var(--the-white)'
              margin='0'
              iconRight
              onClick={() => {
                // Se for a tela de revisão, deve calcular regras de negócio
                if (review) {
                  const isInvalidDateTerms = (date, time) => {
                    const startOfDay = moment().startOf('day')
                    const currentDate = moment()
                    const schedulingDate = moment(date)
                    const schedulingTime = moment(time, 'HH:mm')
                    const schedulingDateTime = schedulingDate.clone().set({
                      hour: schedulingTime.get('hour'),
                      minute: schedulingTime.get('minute')
                    })

                    // Válida se a nova data do agendamento está dentro do tempo limite de captura
                    const invalidDateTerm = schedulingDateTime.diff(startOfDay, 'days') > ONLINE_PAYMENT_DEADLINE

                    // valida se agendamento esta a pelo menos 1 hora da data/hora atual
                    const invalidTimeTerm = schedulingDateTime.diff(currentDate, 'hours') < SCHEDULING_MIN_HOURS

                    return invalidDateTerm || invalidTimeTerm
                  }

                  // Verifica se a loja possui pagamento online ativado
                  const partnerEnabledPayment = (partnerId, partnersData) => {
                    const selectedPartner = partnersData.find(partner => partnerId === partner.id)
                    return selectedPartner?.enabledPayment
                  }

                  // Verifica se o profissional possui pagamento online ativado
                  const professionalEnabledPayment = (professionalId, professionalData) => {
                    const selectedProfessional = professionalData.find(professional => professionalId === professional.id)
                    return selectedProfessional?.enabledPayment
                  }

                  const currentInvalidDateTerms = isInvalidDateTerms(scheduleBody.selectDay, scheduleBody.selectTime)

                  const currentProfessionalPayment = professionalEnabledPayment(scheduleBody.professional_id, professionalData)

                  const currentPartnerPayment = partnerEnabledPayment(scheduleBody.partner_id, partnersData)

                  // Valida se é possível pagar online (para o agendamento atual)
                  const validateCurrentPayment = () => !currentInvalidDateTerms && currentProfessionalPayment && currentPartnerPayment

                  // Valida se é possível pagar online (para os agendamentos anteriores)
                  const validateFormListPayment = () => {
                    // Caso não possua agendamento, retorna como válido
                    if (!formList?.length) {
                      return true
                    }
                    const invalidPayment = formList.some(scheduling => {
                      const { body, data } = scheduling
                      const invalidDateTerm = isInvalidDateTerms(body.selectDay, body.selectTime)
                      const professionalPayment = professionalEnabledPayment(body.professional_id, data.professionalData)
                      const partnerPayment = partnerEnabledPayment(body.partner_id, data.partnersData)

                      return invalidDateTerm || !professionalPayment || !partnerPayment
                    })
                    return !invalidPayment
                  }

                  const shouldShowPaymentOptions = validateCurrentPayment() && validateFormListPayment()

                  const isReschedule = router?.query?.id && router?.query?.reschedule && !hasSchedulingChanged(false, true)

                  if (!shouldShowPaymentOptions || isReschedule) {
                    if (!hasSchedulingChanged()) {
                      router.push(`${PAGE_URL.SCHEDULING_SUMMARY}/${router?.query?.id}`)
                    } else {
                      handleConfirmScheduling({ reschedule: router?.query?.reschedule || false, context, router })
                    }
                  } else {
                    calcNextStep(activeStep)
                  }
                // Se for a tela de definição de pagamento, deve mandar para confirmação do agendamento
                } else if (isHowToPay) {
                  handleConfirmScheduling({ reschedule: router?.query?.reschedule || false, context, router })
                // Se for qualquer outra tela deve avançar o step
                } else {
                  calcNextStep(activeStep)
                }
              }}
            />
          </>
        </div>
      </S.WrapperSchedulingActions>
    </>
  )
}

export default FormSteps
